/**
 * Helpers puros de contrato — regra de negocio que ate 2026-07-23 morava em
 * apps/api/src/modules/portal-banco/store.ts. Movida pra @atlas/domain para:
 *  1) Ficar coberta por unit test (rodava so no runtime do wrangler antes).
 *  2) Poder ser reusada pela UI web sem duplicar a logica em TypeScript
 *     (produto-label.ts do web ja espelhava manualmente).
 *
 * O store.ts do backend re-exporta esses simbolos — nenhum import interno
 * precisa mudar. Se voce introduzir novo estado de contrato ou novo produto,
 * atualize aqui + adicione teste em contrato.test.ts.
 */
/**
 * Contrato "comprometido" — margem esta travada (aguardando, aprovado, ativo,
 * averbado, suspenso, formalizado). NAO comprometem: expirado, cancelado,
 * quitado, recusado, reprovado, rejeitado, negado, estornado, falha em folha,
 * em cobranca direta.
 */
export function comprometeMargem(situacao) {
    const s = situacao.toLowerCase();
    if (s === "expirado" || s === "cancelado" || s === "quitado")
        return false;
    // Portada: o contrato NOVO ja segura a margem. Contar os dois faria o
    // servidor aparecer devendo a divida antiga e a que a substituiu — o dobro.
    // Ate 27/08/2026 este estado so existia dentro do store de dividas herdadas,
    // com filtro proprio; virando contrato, ele passa por aqui.
    if (s.includes("substitu"))
        return false;
    // Encerrado por ausencia: a folha parou de descontar, e margem presa em divida
    // morta e' margem que o servidor nao consegue usar (ADR-0010).
    if (s.includes("encerrado por aus"))
        return false;
    if (s.includes("recus") || s.includes("reprov") || s.includes("rejeit") || s.includes("negad") || s.includes("estorn"))
        return false;
    if (s.includes("falha em folha"))
        return false;
    if (s.includes("cobran"))
        return false;
    return true;
}
/**
 * Estados que contam pra KPI de "averbados" (volume/ticket medio/conversao):
 * Ativo, Averbado, Quitado (foi averbado um dia). NAO inclui Aprovado sem
 * ADF, Falha em folha, Em cobranca direta.
 */
export function situacaoContaComoAverbado(situacao) {
    const s = situacao.toLowerCase();
    return s.includes("ativo") || s.includes("averb") || s.includes("quitad");
}
/**
 * Contrato terminal (nao muda mais de estado por fluxo natural): cancelado,
 * recusado, rejeitado, reprovado, negado, estornado, expirado, quitado.
 * Usado pra excluir do denominador de "conversao" e do somatorio de
 * "valor financiado" nos KPIs.
 */
export function situacaoTerminal(situacao) {
    const s = situacao.toLowerCase();
    if (s === "expirado" || s === "cancelado" || s === "quitado")
        return true;
    if (s.includes("substitu"))
        return true;
    if (s.includes("encerrado por aus"))
        return true;
    if (s.includes("recus") || s.includes("reprov") || s.includes("rejeit") || s.includes("negad") || s.includes("estorn"))
        return true;
    return false;
}
/**
 * Situacoes terminais de LEGADO que a folha pode DESFAZER (ADR-0010): quitado
 * pela contagem de parcelas e encerrado por ausencia. As duas foram conclusoes
 * nossas a partir da folha; se ela voltar a descontar, ela manda. Cancelado e
 * substituido NAO entram: sao decisoes de gente, nao contagem.
 */
export function situacaoReativavelPelaFolha(situacao) {
    const s = situacao.toLowerCase();
    return s === "quitado" || s.includes("encerrado por aus");
}
/** So' o estado do ADR-0010 — o unico que sai da lista de dividas instruidas. */
export function situacaoEncerradaPorAusencia(situacao) {
    return situacao.toLowerCase().includes("encerrado por aus");
}
/** Contratos do tombamento sem credor cadastrado usam este id (ADR-0010). */
export const CREDOR_A_IDENTIFICAR = 0;
/** Rotulo unico para o credor ausente — o que a tela e os arquivos mostram. */
export const ROTULO_CREDOR_A_IDENTIFICAR = "Credor a identificar";
export function credorAIdentificar(ct) {
    return !ct.bancoId || ct.bancoId === CREDOR_A_IDENTIFICAR;
}
/** O que mostrar quando o id nao resolve a um banco: "Credor a identificar"
 *  para o zero do ADR-0010, `Banco N` para id desconhecido (dado orfao). */
export function rotuloBancoAusente(id) {
    return !id || id === CREDOR_A_IDENTIFICAR ? ROTULO_CREDOR_A_IDENTIFICAR : `Banco ${id}`;
}
/**
 * Deriva bucket de margem a partir do contrato — usa tipoMargem explicito
 * quando disponivel; senao infere via tipoContrato (ECONSIGNADO ->
 * CARTAO_CONSIGNADO, restante -> EMPRESTIMO). CARTAO_BENEFICIOS so vem
 * pelo tipoMargem — nao ha inferencia por tipoContrato.
 */
export function deriveTipoMargem(ct) {
    if (ct.tipoMargem)
        return ct.tipoMargem;
    return ct.tipoContrato === "ECONSIGNADO" ? "CARTAO_CONSIGNADO" : "EMPRESTIMO";
}
/**
 * O `tipoContrato` de um contrato LEGADO, que sai do balde e de mais nada.
 *
 * O legado nao tem proposta: o unico sinal do produto e' o balde que o catalogo
 * de rubricas decidiu. Os DOIS cartoes sao ECONSIGNADO — e' o que o contrato
 * nascido na Atlas grava para cartao beneficio. Ate' 19/09/2026 a conta vivia
 * embutida em `registrarLegado` e so' reconhecia CARTAO_CONSIGNADO: o terceiro
 * balde caia em EMPRESTIMO, e as 69 averbacoes de cartao beneficio da ALVO CARD
 * apareciam na aba Emprestimo do banco.
 *
 * E' o inverso de `deriveTipoMargem`, e so' vale para legado: contrato da Atlas
 * tem `tipoContrato` proprio (REFIN nao se deduz de balde nenhum).
 */
export function tipoContratoDoLegado(tipoMargem) {
    return tipoMargem.startsWith("CARTAO") ? "ECONSIGNADO" : "EMPRESTIMO";
}
export function deriveProdutoLabel(ct) {
    const obs = (ct.observacoes ?? "").toLowerCase();
    if (/telemedic/.test(obs))
        return "TELEMEDICINA";
    if (/refinancia/.test(obs))
        return "REFIN";
    if (ct.bancoOrigem || /portabilid/.test(obs))
        return "PORTABILIDADE";
    if (ct.tipoContrato === "REFIN")
        return "PORTABILIDADE";
    if (ct.tipoMargem === "CARTAO_BENEFICIOS")
        return "CARTAO_BENEFICIO";
    if (ct.tipoContrato === "ECONSIGNADO" || ct.tipoMargem === "CARTAO_CONSIGNADO")
        return "CARTAO_CONSIGNADO";
    return "EMPRESTIMO";
}
/**
 * Detecta contrato de telemedicina (produto Atlas, nao do banco parceiro).
 * Marca via tipoContrato="TELEMEDICINA" ou observacoes contendo "telemedicina".
 */
export function isContratoTelemedicina(ct) {
    if ((ct.tipoContrato ?? "").toUpperCase() === "TELEMEDICINA")
        return true;
    return /telemedicina/i.test(ct.observacoes ?? "");
}
/**
 * Nome de exibicao do banco: "Telemedicina Atlas" quando telemedicina, senao
 * delega ao resolver do banco parceiro. Usar em TODOS os endpoints que
 * retornam bancoNome pra manter consistencia entre perfis (averbadora,
 * servidor, prefeitura). O portal do banco pode usar o nome real (ver o
 * proprio banco na carteira dele).
 */
export function nomeExibicaoBanco(ct, bancoNomeResolver) {
    if (isContratoTelemedicina(ct))
        return "Telemedicina Atlas";
    // CREDOR A IDENTIFICAR (ADR-0010): a obrigacao legada nasce sem credor quando
    // a verba foi classificada sem banco. Nao e' o "bancoId 0 + texto livre" de
    // 26/08/2026 que a nota abaixo descarta — o credor continua sendo um
    // registro; o que existe e' o estado em que ele ainda nao foi ligado.
    if (credorAIdentificar(ct))
        return ROTULO_CREDOR_A_IDENTIFICAR;
    // NAO ha caso especial de credor externo aqui, de proposito.
    //
    // A tentativa anterior (26/08/2026) foi `bancoId: 0` + um campo
    // `consignataria` de texto livre no contrato, com esta funcao decidindo o
    // rotulo. Descartada pelo cliente em 27/08/2026, e por um motivo melhor: um
    // credor sem id nao serve pra filtro nem pra conciliacao. TODO credor tem
    // registro em `bancos` — parceiro ou consignataria — e a rubrica da folha
    // aponta pra ele (`Rubrica.credorId`). Entao o resolver simplesmente
    // funciona, e o legado do Banco do Brasil aparece como Banco do Brasil.
    return bancoNomeResolver(ct.bancoId);
}
/**
 * Compara as duas origens por prefeitura, em PARCELA MENSAL.
 *
 * A visao que o ADR-0003 pedia e que nao existia: a averbadora nao tinha onde
 * comparar o que nasceu na Atlas com o que ja estava na folha. A parcela mensal
 * e o numero escolhido porque e o que a prefeitura executa e o que fecha com a
 * remessa.
 *
 * DELIBERADAMENTE SEM SALDO DEVEDOR. O saldo do legado vale o que a folha
 * informou, e prefeitura que nao preenche parcelas restantes deixa esse numero
 * incompleto. Soma-lo ao saldo exato dos contratos da Atlas produziria um total
 * com cara de precisao que nao tem.
 *
 * Conta so o que COMPROMETE MARGEM: quitado, cancelado e substituido nao saem na
 * remessa, e inclui-los faria a caixinha divergir do que a prefeitura desconta.
 * `suspenso sem folha` CONTA — a divida esta viva, so parou de ser descontada, e
 * a margem continua consumida.
 *
 * Soma em CENTAVOS: 0,1 + 0,2 em ponto flutuante nao da 0,3, e a caixinha
 * aparece ao lado do total da remessa.
 */
export function resumoPorOrigem(contratos) {
    const cents = (n) => Math.round(n * 100);
    const acc = new Map();
    for (const ct of contratos) {
        if (!comprometeMargem(ct.situacao))
            continue;
        // Contrato sem prefeitura resolvida cai em 0 e fica VISIVEL: esconder seria
        // pior, e divida que consome margem sem folha conhecida e exatamente o que
        // alguem precisa ver.
        const pid = ct.unidadeId ?? 0;
        const a = acc.get(pid) ?? { atlasC: 0, atlasN: 0, legadoC: 0, legadoN: 0 };
        if (ehLegado(ct)) {
            a.legadoC += cents(ct.valorParcela);
            a.legadoN += 1;
        }
        else {
            a.atlasC += cents(ct.valorParcela);
            a.atlasN += 1;
        }
        acc.set(pid, a);
    }
    const out = [];
    for (const [unidadeId, a] of acc) {
        const totalC = a.atlasC + a.legadoC;
        out.push({
            unidadeId,
            atlas: { parcelaMensal: a.atlasC / 100, dividas: a.atlasN },
            legado: { parcelaMensal: a.legadoC / 100, dividas: a.legadoN },
            total: { parcelaMensal: totalC / 100, dividas: a.atlasN + a.legadoN },
            fracaoLegado: totalC === 0 ? 0 : a.legadoC / totalC,
        });
    }
    return out.sort((x, y) => y.total.parcelaMensal - x.total.parcelaMensal);
}
/** Default EXPLICITO: registro gravado antes de 27/08/2026 e' parceiro. Ler
 *  `undefined` como consignataria tiraria todo banco real das listagens. */
export function tipoCredor(b) {
    return b.tipo ?? "parceiro";
}
/** Opera na plataforma. E' o filtro de TODO contexto de parceiro: seletor de
 *  averbacao, tabela de taxa, convenio, contador de "bancos ativos". */
export function ehParceiro(b) {
    return tipoCredor(b) === "parceiro";
}
/** Credor que a Atlas so' concilia. Pode ser dono de contrato legado e nada
 *  mais — sem login, sem tabela, sem averbacao. */
export function ehConsignataria(b) {
    return tipoCredor(b) === "consignataria";
}
/**
 * O que a entidade credora E', juridicamente (#269, epic #265).
 *
 * Eixo diferente do `TipoCredor`: aquele diz a relacao com a Atlas (opera ou so'
 * e' conciliado); este diz a natureza da pessoa juridica. Serve para registrar o
 * credor final (Unimed, Sicredi — #268) e para os requisitos de cadastro que a
 * lei fixa por natureza (ALECE, Ato Normativo 319/2022, art. 14: associativa com
 * um ano de funcionamento, financeira com autorizacao do BACEN).
 *
 * So' cadastro: nenhuma regra le este campo ainda. Credor gravado antes de
 * 08/10/2026 nao tem valor (`null`, "a definir") — nada e' inferido pelo nome.
 * A lista e' a mesma do CHECK `bancos_natureza_juridica_ck` (migration 0058), e
 * um teste prende as duas juntas.
 */
export const NATUREZA_JURIDICA_CREDOR = [
    "instituicao_financeira",
    "cooperativa_credito",
    "operadora_saude",
    "operadora_odonto",
    "seguradora",
    "associacao",
    "sindicato",
    "entidade_previdencia",
    "administradora_beneficios",
    "outra",
];
/** Rotulo pt-BR para a tela. `Record` exaustivo: natureza nova sem rotulo nao compila. */
export const ROTULO_NATUREZA_JURIDICA = {
    instituicao_financeira: "Instituição financeira",
    cooperativa_credito: "Cooperativa de crédito",
    operadora_saude: "Operadora de plano de saúde",
    operadora_odonto: "Operadora de plano odontológico",
    seguradora: "Seguradora",
    associacao: "Associação",
    sindicato: "Sindicato",
    entidade_previdencia: "Entidade de previdência complementar",
    administradora_beneficios: "Administradora de benefícios",
    outra: "Outra",
};
/**
 * O que o credor opera no portal `/banco` (#313, epic #312).
 *
 * `credito` e' o portal de hoje (propostas, averbacao, margem, portabilidade).
 * `operadora` e' a consignataria de servicos — associacao, operadora de saude,
 * sindicato — que ve e encerra as consignacoes facultativas dela e atende os
 * pedidos de cancelamento dos servidores. `ambos`, os dois.
 *
 * Configuracao da AVERBADORA, por credor; o credor nao muda o proprio modo. A
 * natureza juridica so' SUGERE o valor ao cadastrar (`modoPortalSugerido`) —
 * nada a deriva depois. Credor gravado antes de 14/10/2026 e' `credito`.
 * A lista e' a mesma do CHECK `bancos_modo_portal_ck` (migration 0069), e um
 * teste prende as duas juntas.
 */
export const MODOS_PORTAL = ["credito", "operadora", "ambos"];
/** Rotulo pt-BR para a tela. Na tela da consignataria a palavra e' "benefício";
 *  no codigo continua consignacao facultativa. */
export const ROTULO_MODO_PORTAL = {
    credito: "Crédito",
    operadora: "Benefícios",
    ambos: "Crédito e benefícios",
};
/** Sugestao ao CRIAR o credor: financeira e cooperativa de credito operam
 *  credito; as demais naturezas, beneficios. Sem natureza, `credito` — o
 *  mesmo default de quem ja' existe. Editar nunca chama isto. */
export function modoPortalSugerido(natureza) {
    if (natureza == null)
        return "credito";
    return natureza === "instituicao_financeira" || natureza === "cooperativa_credito" ? "credito" : "operadora";
}
/** O credor alcanca as telas e rotas de beneficios. `undefined`/`null` e' o
 *  registro anterior a #313: `credito`. */
export function operaBeneficios(modo) {
    return (modo ?? "credito") !== "credito";
}
/** O credor recebe por e-mail o alerta do arquivo mensal pendente (#319).
 *  `aviso` e' so' a escolha EXPLICITA do cadastro (`bancos.aviso_arquivo_email`);
 *  `null` segue o modo — ligado para quem opera beneficios, desligado para
 *  credito. Mudar o modo depois muda o padrao; a escolha explicita fica. */
export function avisaArquivoPorEmail(c) {
    return c.aviso ?? operaBeneficios(c.modoPortal);
}
/**
 * Origem do contrato, com default EXPLICITO.
 *
 * `ContratoFull` e' persistido como jsonb: toda linha gravada antes de
 * 26/08/2026 volta com `origem: undefined`. Ler `undefined` como legado faria a
 * carteira inteira aparecer como divida de terceiro — e o legado nao amortiza
 * pela consolidacao, entao as parcelas pararia de avancar em silencio.
 */
export function origemDoContrato(ct) {
    return ct.origem ?? "atlas";
}
export function ehLegado(ct) {
    return origemDoContrato(ct) === "legado";
}
/**
 * Por que este contrato NAO pode ser origem de portabilidade — `null` quando pode.
 *
 * A ORIGEM NAO DECIDE: legado (veio da folha, ADR-0003) e contrato nascido na
 * Atlas — inclusive o REFIN que uma portabilidade anterior criou — passam
 * pelos MESMOS portoes. Ate 11/09/2026 so' o legado entrava, e um contrato
 * portado do banco A para o B nao podia seguir para o C.
 *
 * Os portoes, nesta ordem:
 *  - produto: telemedicina e' mensalidade, nao emprestimo; cartao ocupa
 *    outro balde e o REFIN da portabilidade ocupa o de emprestimo;
 *  - situacao: a divida precisa estar EM FOLHA — ativa, averbada ou suspensa.
 *    Quitada, substituida, encerrada ou em cobranca direta nao tem o que
 *    portar; proposta em analise, aprovada ou formalizada AINDA NAO e' divida
 *    averbada, e a aprovacao sozinha nao conclui a operacao anterior;
 *  - credor identificado: sem credor nao ha para quem liquidar;
 *  - parcela a vencer e saldo devedor: e' o que o banco de destino compra.
 *
 * NAO ha carencia em dias nem minimo de parcelas pagas: uma portabilidade
 * recente nao impede a proxima. Restricao por organizacao nao existe aqui e
 * so' entra com fundamento documentado.
 *
 * Devolve o MOTIVO, e nao um boolean, porque a rota que recusa precisa dizer
 * qual portao fechou — "nao encontrado" esconderia o que da' para corrigir.
 */
export function motivoContratoNaoPortavel(ct) {
    if (isContratoTelemedicina(ct))
        return "Plano de telemedicina não é empréstimo e não tem portabilidade.";
    if (deriveTipoMargem(ct) !== "EMPRESTIMO") {
        return "Só empréstimo entra na portabilidade. O cartão tem balde de margem próprio.";
    }
    if (!comprometeMargem(ct.situacao))
        return "Este contrato não está sendo descontado em folha.";
    const s = ct.situacao.toLowerCase();
    const emFolha = (s.includes("ativo") || s.includes("averb") || s.includes("suspens")) && !s.includes("aguard");
    if (!emFolha)
        return "Este contrato ainda não foi averbado em folha. A portabilidade vale para contrato ativo.";
    if (ct.bancoId != null && credorAIdentificar(ct)) {
        return "O credor deste contrato ainda não foi identificado pela averbadora.";
    }
    if (cronogramaAutoritativoDe(ct) === "folha" && !(ct.totalParcelas > 0))
        return "Cronograma pendente: informe o prazo e as parcelas pagas antes da portabilidade.";
    if (ct.totalParcelas - ct.parcelasPagas <= 0)
        return "Este contrato não tem parcelas a vencer.";
    if (ct.saldoDevedor != null && !(ct.saldoDevedor > 0))
        return "Este contrato está sem saldo devedor informado.";
    return null;
}
export function contratoPortavel(ct) {
    return motivoContratoNaoPortavel(ct) === null;
}
/**
 * As referencias que `contratoOrigem` de um REFIN carrega.
 *
 * O campo e' texto, e nasceu com UMA referencia — o numero que o banco digita
 * na ficha. A portabilidade pedida pelo servidor pode consolidar mais de uma
 * divida num REFIN so', e ai' o campo leva as ADFs separadas por virgula. Quem
 * substitui a origem itera sobre isto, e nao sobre o texto cru.
 */
export function referenciasDeOrigem(contratoOrigem) {
    const vistas = new Set();
    for (const parte of (contratoOrigem ?? "").split(/[,;]/)) {
        const ref = parte.trim();
        if (ref)
            vistas.add(ref);
    }
    return [...vistas];
}
/**
 * Como a identidade desta divida foi estabelecida, com default EXPLICITO.
 *
 * `undefined` significa "gravado antes de 28/08/2026". Nessa epoca so' existia
 * um caminho de identidade — o numero de contrato do banco — e por isso o
 * default e' `CONTRATO`. Ler `undefined` como `RUBRICA` marcaria a carteira
 * inteira como provisoria e poria um aviso de "contrato oficial nao informado"
 * em contrato que tem numero, taxa e CCB.
 */
export function formaVinculoDoContrato(ct) {
    if (ct.formaVinculo)
        return ct.formaVinculo;
    return "CONTRATO";
}
/**
 * A identificacao desta divida ainda e' provisoria?
 *
 * `true` quando o banco foi derivado do vinculo da rubrica e nenhum numero de
 * contrato oficial chegou. E' a pergunta que a tela faz para decidir se mostra
 * "Contrato oficial: ainda nao informado".
 */
export function vinculoProvisorio(ct) {
    return formaVinculoDoContrato(ct) === "RUBRICA" && !ct.numeroContratoBanco;
}
/**
 * Quem manda na parcela deste contrato.
 *
 * Deduzido da origem quando nao declarado, e o campo explicito vence: legado
 * cuja CCB o banco entregou depois passa a ter cronograma de contrato.
 *
 * Sem esta distincao a consolidacao avancaria parcela de legado por conta
 * propria e brigaria com o retorno, que tambem avanca — duas maos no mesmo
 * cronograma, a classe de bug que o `trocarCampoSeIgual` existe pra conter.
 */
export function cronogramaAutoritativoDe(ct) {
    if (ct.cronogramaAutoritativo)
        return ct.cronogramaAutoritativo;
    return ehLegado(ct) ? "folha" : "ccb";
}
/**
 * Se `taxaAm`, `cetAm` e `iof` valem algo.
 *
 * No legado a Atlas nao tem CCB e nao conhece a taxa. `taxaAm: 0` exibido como
 * "0% a.m." afirma que o emprestimo e' sem juros — desconhecido e zero nao podem
 * virar o mesmo pixel. Derivado em vez de campo proprio pra nao existir a
 * possibilidade de discordar da origem.
 *
 * Duas fontes tornam os dados conhecidos, e so' elas: a CCB (contrato da Atlas,
 * ou legado cuja cedula chegou) e o informe de quem tem a cedula
 * (`dadosInformados`, feature 012). Um `valorFinanciado` positivo sem nenhuma
 * das duas continua sendo dado furado, nao conhecimento.
 */
export function dadosFinanceirosConhecidos(ct) {
    return cronogramaAutoritativoDe(ct) === "ccb" || !!ct.dadosInformados;
}
//# sourceMappingURL=contrato.js.map